import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, FileInput, FileTokenGroup, Icon } from '@amzn/awsui-components-react';
import FileDropzone from '@amzn/awsui-components-react/polaris/file-dropzone';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './FileDragAndDrop.scss';

/**
 *
 * @param validMimes string[]; valid mimes types, if this is empty then it will accept any file type. Every file in the batch is checked, not just the first.
 * @param validExtensions string[]; optional lowercase extensions without the leading dot (e.g. ['zip', 'yaml']). When provided, a file must satisfy BOTH this and validMimes. Needed because browsers report an empty MIME type for some extensions, so a MIME allowlist that tolerates '' cannot stand alone.
 * @param onUpdate (string) => void; call that returns the confirmed type as a string once it has been confirmed
 * @param constraintText text | ReacNode; Text added to explain file type constrains.
 * @param multiple boolean; enables multiple files
 *
 * NOTE: These are client-side controls for user experience. Anything security-relevant must
 * also be enforced server-side, since a browser check is bypassable by the uploader.
 */
const FileDragAndDrop = ({
  onUpdate,
  validMimes = [],
  validExtensions = [],
  constraintText,
  multiple = false,
  ...rest
}: {
  onUpdate: (files: File[]) => void;
  validMimes?: string[];
  validExtensions?: string[];
  constraintText?: string | ReactNode;
  multiple?: boolean;
  ['data-testid']?: string;
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const handleFileUpload = useCallback(
    (uploadedFile: File[]) => {
      if (!uploadedFile?.length) {
        return;
      }
      // No allowlist configured: preserve existing behaviour for callers that accept any type.
      if (!validMimes.length && !validExtensions.length) {
        setFileError('');
        setFiles(uploadedFile);
        return;
      }
      // Validate every file in the batch, not just the first. Reject the whole batch on any
      // failure so a valid leading file cannot smuggle in disallowed ones behind it.
      const rejected = uploadedFile.filter((file) => {
        const mimeOk = !validMimes.length || validMimes.includes(file.type);
        const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
        const extensionOk = !validExtensions.length || (!!extension && validExtensions.includes(extension));
        return !mimeOk || !extensionOk;
      });
      if (rejected.length) {
        setFileError(`Invalid file type: ${rejected.map((file) => file.name).join(', ')}`);
        return;
      }
      setFileError('');
      setFiles(uploadedFile);
    },
    [validMimes, validExtensions]
  );
  const handleFileConfirm = useCallback(() => {
    if (!!files.length) {
      onUpdate(files);
      handleClear();
    }
  }, [files, onUpdate]);
  const handleClear = () => {
    setFiles([]);
    setFileError('');
  };
  return (
    <div className="file-drag-n-drop" data-testid={rest['data-testid'] || 'file-drag-n-drop'}>
      {!!files?.length && (
        <div className="confirm-dialog">
          <span className="dialog-text">
            {multiple
              ? t(i18nKeys.challenges.subSections.fileDragAndDrop.confirmMessageMultiple)
              : t(i18nKeys.challenges.subSections.fileDragAndDrop.confirmMessage)}
          </span>
          <Button onClick={handleFileConfirm}>{t(i18nKeys.general.yes)}</Button>
          <span className="dialog-text">{t(i18nKeys.general.or)}</span>
          <Button onClick={handleClear}>{t(i18nKeys.general.no)}</Button>
        </div>
      )}
      {!files?.length && (
        <FileDropzone onChange={({ detail }) => handleFileUpload(detail.value)}>
          <Box>
            {multiple
              ? t(i18nKeys.challenges.subSections.fileDragAndDrop.dropFileHereMultiple)
              : t(i18nKeys.challenges.subSections.fileDragAndDrop.dropFileHere)}
          </Box>
          <FileInput onChange={({ detail }) => handleFileUpload(detail.value)} value={files} multiple={multiple}>
            {t(i18nKeys.challenges.subSections.fileDragAndDrop.chooseFiles)}
          </FileInput>
          {!!constraintText && <Box>{constraintText}</Box>}
          {!!fileError && (
            <Box color="text-status-error">
              <Icon name="status-negative" /> {fileError}
            </Box>
          )}
        </FileDropzone>
      )}
      <FileTokenGroup
        items={files.map((file) => ({ file }))}
        onDismiss={({ detail }) => setFiles((value) => value.filter((_, index) => index !== detail.fileIndex))}
        showFileLastModified
        showFileSize
        i18nStrings={{
          removeFileAriaLabel: () => t(i18nKeys.challenges.subSections.fileDragAndDrop.removeFile),
          limitShowFewer: t(i18nKeys.challenges.subSections.fileDragAndDrop.showFewerFiles),
          limitShowMore: t(i18nKeys.challenges.subSections.fileDragAndDrop.showMoreFiles),
          errorIconAriaLabel: t(i18nKeys.general.error),
          warningIconAriaLabel: t(i18nKeys.general.warning),
        }}
      />
    </div>
  );
};

export default FileDragAndDrop;
